import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Barcode, QrCode, Printer, Maximize2, Minimize2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const barcodeUrl = (code, fmt) => `${BACKEND}/api/barcode/${encodeURIComponent(code)}${fmt === 'qr' ? '?fmt=qr' : ''}`;
const esc = (s) => String(s || '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));

// Print a full sheet of barcode/QR labels for many products at once (shelf labelling).
export const BarcodeSheetDialog = ({ items = [], onClose }) => {
  const candidates = useMemo(() => items.filter((it) => it.code), [items]);
  const [fmt, setFmt] = useState('code128');
  const [cols, setCols] = useState('3');
  const [size, setSize] = useState('normal');
  const [selected, setSelected] = useState(() => new Set(candidates.map((c) => c.code)));

  const allChecked = selected.size === candidates.length && candidates.length > 0;
  const toggle = (code) => setSelected((prev) => { const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(candidates.map((c) => c.code)));

  const print = () => {
    const chosen = candidates.filter((c) => selected.has(c.code));
    if (!chosen.length) return;
    const compact = size === 'compact';
    const m = compact
      ? { pad: 3, gap: 4, img: fmt === 'qr' ? 54 : 34, title: 9, code: 9, sub: 8, radius: 4 }
      : { pad: 8, gap: 8, img: fmt === 'qr' ? 90 : 54, title: 11, code: 11, sub: 10, radius: 6 };
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    const cells = chosen.map((c) => `<div class="label"><img src="${barcodeUrl(c.code, fmt)}" alt="${esc(c.code)}"/><div class="code">${esc(c.code)}</div><div class="title">${esc(c.title)}</div>${c.subtitle ? `<div class="sub">${esc(c.subtitle)}</div>` : ''}</div>`).join('');
    w.document.write(`<!doctype html><html><head><title>Hoja de etiquetas (${chosen.length})</title>
      <style>@page{margin:${compact ? '5mm' : '8mm'}} body{font-family:Arial,Helvetica,sans-serif;margin:0;padding:6px}
      .grid{display:grid;grid-template-columns:repeat(${cols},1fr);gap:${m.gap}px}
      .label{border:1px solid #e5e5e5;border-radius:${m.radius}px;padding:${m.pad}px;text-align:center;page-break-inside:avoid;break-inside:avoid}
      .label img{max-width:100%;height:auto;max-height:${m.img}px}
      .code{font-size:${m.code}px;letter-spacing:1px;margin-top:2px}
      .title{font-size:${m.title}px;font-weight:700;margin-top:2px;line-height:1.15}
      .sub{font-size:${m.sub}px;color:#666}</style></head>
      <body onload="window.print();setTimeout(()=>window.close(),400)"><div class="grid">${cells}</div></body></html>`);
    w.document.close();
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="seller-dialog max-w-lg" data-testid="barcode-sheet-dialog">
        <DialogHeader>
          <DialogTitle>Hoja de etiquetas</DialogTitle>
          <DialogDescription>Selecciona los productos e imprime una hoja con todos sus códigos para etiquetar una estantería completa de una vez.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <ToggleGroup type="single" value={fmt} onValueChange={(v) => v && setFmt(v)} data-testid="barcode-sheet-format">
            <ToggleGroupItem value="code128" data-testid="barcode-sheet-fmt-code128" aria-label="Barras"><Barcode className="h-4 w-4" /> Barras</ToggleGroupItem>
            <ToggleGroupItem value="qr" data-testid="barcode-sheet-fmt-qr" aria-label="QR"><QrCode className="h-4 w-4" /> QR</ToggleGroupItem>
          </ToggleGroup>
          <ToggleGroup type="single" value={size} onValueChange={(v) => { if (v) { setSize(v); setCols(v === 'compact' ? '4' : '3'); } }} data-testid="barcode-sheet-size">
            <ToggleGroupItem value="normal" data-testid="barcode-sheet-size-normal" aria-label="Tamaño normal"><Maximize2 className="h-4 w-4" /> Normal</ToggleGroupItem>
            <ToggleGroupItem value="compact" data-testid="barcode-sheet-size-compact" aria-label="Tamaño compacto"><Minimize2 className="h-4 w-4" /> Compacto</ToggleGroupItem>
          </ToggleGroup>
          <ToggleGroup type="single" value={cols} onValueChange={(v) => v && setCols(v)} data-testid="barcode-sheet-cols">
            {['2', '3', '4', '5', '6'].map((c) => <ToggleGroupItem key={c} value={c} data-testid={`barcode-sheet-cols-${c}`} aria-label={`${c} columnas`}>{c}</ToggleGroupItem>)}
          </ToggleGroup>
        </div>

        <div className="flex items-center gap-2 border-b border-border pb-2">
          <Checkbox id="sheet-all" checked={allChecked} onCheckedChange={toggleAll} data-testid="barcode-sheet-select-all" />
          <label htmlFor="sheet-all" className="text-sm font-medium cursor-pointer">Seleccionar todo ({candidates.length})</label>
          <span className="ml-auto text-xs text-muted-foreground" data-testid="barcode-sheet-selected-count">{selected.size} seleccionados</span>
        </div>

        <ScrollArea className="h-64 pr-3">
          <div className="space-y-1">
            {candidates.length ? candidates.map((c) => (
              <label key={c.code} className="flex items-center gap-3 rounded-md p-2 hover:bg-muted/60 cursor-pointer" data-testid={`barcode-sheet-item-${c.code}`}>
                <Checkbox checked={selected.has(c.code)} onCheckedChange={() => toggle(c.code)} data-testid={`barcode-sheet-check-${c.code}`} />
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{c.title}</span><span className="block text-xs text-muted-foreground">{c.code}{c.subtitle ? ` · ${c.subtitle}` : ''}</span></span>
              </label>
            )) : <p className="text-sm text-muted-foreground p-4 text-center" data-testid="barcode-sheet-empty">Ningún producto del filtro actual tiene SKU. Genera los SKU faltantes primero.</p>}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" data-testid="barcode-sheet-cancel" onClick={onClose}>Cerrar</Button>
          <Button className="seller-primary" data-testid="barcode-sheet-print" disabled={!selected.size} onClick={print}><Printer className="h-4 w-4" /> Imprimir {selected.size ? `(${selected.size})` : ''}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
